import { getWrestlerName, getWrestlerNameUpper, wrestlerMatchesSearch, sortWrestlersByName } from './wrestlerUtils';
import CustomWrestlerEditor from './CustomWrestlerEditor';
import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import UnifiedWrestlerCard, { WrestlerCardStyles } from './WrestlerCard';

const placeholder = process.env.PUBLIC_URL + '/wrestlers/placeholder.png';
const getWrestlerRender = (w) => {
  if (!w) return null;
  if (w.customRender) return w.customRender;
  if (w.wrestler_id) return process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png';
  return null;
};
const getClassIcon = (cls) => cls ? process.env.PUBLIC_URL + '/icons/class-' + (cls || '').toLowerCase() + '.png' : null;
const getRoleIcon = (heel) => process.env.PUBLIC_URL + '/icons/role-' + (heel ? 'heel' : 'face') + '.png';
const getXPBadge = (xp) => process.env.PUBLIC_URL + '/icons/xp-badge-' + Math.min(Math.floor((xp || 0) / 20), 5) + '.png';
const getGenderIcon = (gender) => process.env.PUBLIC_URL + '/icons/gender-' + (gender === 'M' ? 'male' : 'female') + '.png';
const GENDER_LABEL = (gender) => gender === 'M' ? '♂' : '♀';

const BRAND_COLORS = { 'Raw': '#c0392b', 'SmackDown': '#1a6bb5', 'NXT': '#a8a9ad', 'NXT 2.0': '#e91e8c', 'ECW': '#7b1fa2', 'WCW': '#e65100', 'NXT Mutiny': '#424242' };
const STATUS_COLORS = { active: '#2ecc71', contract: '#2ecc71', local: '#e67e22', inactive: '#888' };
const STATUS_LABELS = { active: 'ACTIVE', contract: 'CONTRACT', local: 'LOCAL', inactive: 'INACTIVE' };

const CLASSES = ['Fighter', 'Bruiser', 'Cruiser', 'Specialist', 'Giant'];
const XP_LEVELS = [
  { label: 'LVL 1', badge: 1, min: 0, max: 9, color: '#888' },
  { label: 'LVL 2', badge: 2, min: 10, max: 14, color: '#cd7f32' },
  { label: 'LVL 3', badge: 3, min: 15, max: 19, color: '#a8a9ad' },
  { label: 'LVL 4', badge: 4, min: 20, max: 24, color: '#d4af37' },
  { label: 'LVL 5', badge: 5, min: 25, max: 999, color: '#e74c3c' },
];

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .recruit-card { transition: background 0.15s, border-color 0.15s, transform 0.15s; cursor: pointer; }
  .recruit-card:hover { border-color: rgba(255,255,255,0.35) !important; transform: translateY(-2px); }
  .recruit-row:hover { background: rgba(255,255,255,0.04) !important; }
  .icon-cell { transition: background 0.12s, border-color 0.12s; cursor: pointer; border-radius: 8px; }
  .icon-cell:hover { background: rgba(255,255,255,0.1) !important; border-color: rgba(255,255,255,0.4) !important; }
  .contract-bar { transition: background 0.12s, border-color 0.12s; cursor: pointer; border-radius: 5px; }
  .contract-bar:hover { border-color: rgba(255,255,255,0.4) !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

const S = {
  anton: { fontFamily: "'Anton', sans-serif" },
  mono: { fontFamily: "'Monda', sans-serif" },
  panel: { background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px' },
};

// ── Icon Cell (for CLASS, RING LVL, ROLE, GENRE) ───────────────────
const IconCell = ({ icon, label, active, color, onClick, size }) => {
  var sz = size || 52;
  return (
    <div className="icon-cell" onClick={onClick}
      style={{ width: sz + 'px', height: sz + 'px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', background: active ? (color ? color + '22' : 'rgba(255,255,255,0.12)') : 'rgba(255,255,255,0.04)', border: '1px solid ' + (active ? (color || 'rgba(255,255,255,0.5)') : 'rgba(255,255,255,0.1)') }}>
      {icon && <img loading="lazy" src={icon} alt={label} title={label} style={{ width: (sz * 0.5) + 'px', height: (sz * 0.5) + 'px', objectFit: 'contain', opacity: active ? 1 : 0.45 }} onError={e => { e.target.style.display='none'; }} />}
      {!icon && label && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: active ? '#fff' : 'rgba(255,255,255,0.4)', letterSpacing: '0.5px', textAlign: 'center', lineHeight: '1.2', padding: '2px' }}>{label}</div>}
    </div>
  );
};

// ── Search Block ──────────────────────────────────────────────────────
const SearchBlock = ({ filterClass, setFilterClass, filterXP, setFilterXP, filterRole, setFilterRole, filterGender, setFilterGender, filterContract, setFilterContract, brandColor }) => {
  var blockStyle = { background: 'rgba(6,6,6,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px 14px' };
  var titleStyle = { fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px', textAlign: 'center' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' }}>SEARCH BY</div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>

        {/* CLASS */}
        <div style={blockStyle}>
          <div style={titleStyle}>CLASS</div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {CLASSES.map(function(cls) { return (
              <IconCell key={cls} icon={getClassIcon(cls)} label={cls} active={filterClass === cls} color={brandColor} onClick={() => setFilterClass(filterClass === cls ? 'ALL' : cls)} />
            ); })}
          </div>
        </div>

        {/* RING LVL */}
        <div style={blockStyle}>
          <div style={titleStyle}>RING LVL</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 52px)', gap: '6px' }}>
            {XP_LEVELS.map(function(lvl) { return (
              <IconCell key={lvl.label} icon={process.env.PUBLIC_URL + '/icons/xp-badge-' + lvl.badge + '.png'} label={lvl.label} active={filterXP === lvl.label} color={lvl.color} onClick={() => setFilterXP(filterXP === lvl.label ? 'ALL' : lvl.label)} size={52} />
            ); })}
          </div>
        </div>

        {/* ROLE + GENRE */}
        <div style={blockStyle}>
          <div style={titleStyle}>ROLE</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 52px)', gap: '6px', marginBottom: '10px' }}>
            <IconCell icon={getRoleIcon(false)} label="FACE" active={filterRole === 'FACE'} color="#2ecc71" onClick={() => setFilterRole(filterRole === 'FACE' ? 'ALL' : 'FACE')} />
            <IconCell icon={getRoleIcon(true)} label="HEEL" active={filterRole === 'HEEL'} color="#e74c3c" onClick={() => setFilterRole(filterRole === 'HEEL' ? 'ALL' : 'HEEL')} />
          </div>
          <div style={titleStyle}>GENRE</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 52px)', gap: '6px' }}>
            <div className="icon-cell" onClick={() => setFilterGender(filterGender === 'MALE' ? 'ALL' : 'MALE')}
              style={{ width: '52px', height: '52px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', background: filterGender === 'MALE' ? 'rgba(52,152,219,0.22)' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (filterGender === 'MALE' ? '#3498db' : 'rgba(255,255,255,0.1)'), borderRadius: '8px', cursor: 'pointer' }}>
              <span style={{ fontSize: '1.4rem', color: filterGender === 'MALE' ? '#3498db' : 'rgba(255,255,255,0.35)' }}>♂</span>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: filterGender === 'MALE' ? '#3498db' : 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>MALE</div>
            </div>
            <div className="icon-cell" onClick={() => setFilterGender(filterGender === 'FEMALE' ? 'ALL' : 'FEMALE')}
              style={{ width: '52px', height: '52px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px', background: filterGender === 'FEMALE' ? 'rgba(233,30,140,0.22)' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (filterGender === 'FEMALE' ? '#e91e8c' : 'rgba(255,255,255,0.1)'), borderRadius: '8px', cursor: 'pointer' }}>
              <span style={{ fontSize: '1.4rem', color: filterGender === 'FEMALE' ? '#e91e8c' : 'rgba(255,255,255,0.35)' }}>♀</span>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.34rem', color: filterGender === 'FEMALE' ? '#e91e8c' : 'rgba(255,255,255,0.3)', letterSpacing: '0.5px' }}>FEMALE</div>
            </div>
          </div>
        </div>

        {/* CONTRACT TYPE */}
        <div style={blockStyle}>
          <div style={titleStyle}>CONTRACT TYPE</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {[
              { key: 'ALL', label: 'ALL', color: 'rgba(255,255,255,0.3)' },
              { key: 'ACTIVE', label: 'ACTIVE', color: STATUS_COLORS.active },
              { key: 'LOCAL', label: 'LOCAL', color: STATUS_COLORS.local },
              { key: 'CONTRACT', label: 'CONTRACT', color: STATUS_COLORS.contract },
            ].map(function(s) { return (
              <div key={s.key} className="contract-bar" onClick={() => setFilterContract(s.key)}
                style={{ width: '160px', height: '32px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px', background: filterContract === s.key ? s.color + '22' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (filterContract === s.key ? s.color : 'rgba(255,255,255,0.1)') }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: filterContract === s.key ? '#fff' : 'rgba(255,255,255,0.4)', letterSpacing: '1px' }}>{s.label}</div>
              </div>
            ); })}
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Recruit Card ──────────────────────────────────────────────────────
const RecruitCard = ({ w, brandColor, onClick }) => {
  var statusColor = STATUS_COLORS[w.status] || '#888';
  return (
    <div className="recruit-card" onClick={() => onClick(w)}
      style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', cursor: 'pointer' }}>
      {/* Render area */}
      <div style={{ height: '220px', overflow: 'hidden', position: 'relative', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '22 0%, transparent 70%)' }}>
        <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')}
          style={{ position: 'absolute', bottom: '-15%', left: '50%', transform: 'translateX(-50%)', height: '280px', width: 'auto', objectFit: 'contain', objectPosition: 'bottom' }} />
      </div>
      {/* Info bar */}
      <div style={{ padding: '8px 10px 10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', color: '#fff', marginBottom: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getWrestlerNameUpper(w, 'display')}</div>
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '6px' }}>
          {w.fight_class && <img loading="lazy" src={getClassIcon(w.fight_class)} alt={w.fight_class} title={w.fight_class} style={{ width: '18px', height: '18px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />}
          <img loading="lazy" src={getRoleIcon(w.heel)} alt="" style={{ width: '18px', height: '18px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
          <div style={{ position: 'relative', width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
            <span style={{ position: 'absolute', fontFamily: "'Anton',sans-serif", fontSize: '6px', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.9)', pointerEvents: 'none' }}>{w.starting_xp || 0}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: statusColor, background: statusColor + '18', border: '1px solid ' + statusColor + '44', borderRadius: '2px', padding: '1px 5px' }}>{STATUS_LABELS[w.status] || w.status}</div>
          <button onClick={e => { e.stopPropagation(); onClick(w); }}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.35)', color: '#2ecc71', padding: '2px 7px', borderRadius: '3px', cursor: 'pointer' }}>
            SIGN
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Recruit Row (List) ────────────────────────────────────────────────
const RecruitRow = ({ w, brandColor, onClick }) => {
  var statusColor = STATUS_COLORS[w.status] || '#888';
  return (
    <div className="recruit-row" onClick={() => onClick(w)}
      style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }}>
      <div style={{ width: '36px', height: '48px', overflow: 'hidden', flexShrink: 0 }}>
        <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt={getWrestlerName(w, 'display')} style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'top' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', letterSpacing: '1px', color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getWrestlerNameUpper(w, 'display')}</div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginTop: '3px' }}>
          {w.fight_class && <img loading="lazy" src={getClassIcon(w.fight_class)} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />}
          <img loading="lazy" src={getRoleIcon(w.heel)} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
          <img loading="lazy" src={getGenderIcon(w.gender)} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
          <div style={{ position: 'relative', width: '16px', height: '16px' }}>
            <img loading="lazy" src={getXPBadge(w.starting_xp || 0)} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Anton',sans-serif", fontSize: '5px', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.9)' }}>{w.starting_xp || 0}</span>
          </div>
        </div>
      </div>
      <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: statusColor, background: statusColor + '18', border: '1px solid ' + statusColor + '44', borderRadius: '2px', padding: '1px 6px', flexShrink: 0 }}>{STATUS_LABELS[w.status] || w.status}</div>
      <button onClick={e => { e.stopPropagation(); onClick(w); }}
        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.35)', color: '#2ecc71', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}>
        SIGN
      </button>
    </div>
  );
};

// ── Recruit Modal ─────────────────────────────────────────────────────
const RecruitModal = ({ wrestler, brandColor, budget, week, onConfirm, onClose, userId }) => {
  const fileInputRef = React.useRef(null);
  const [contractPrice, setContractPrice] = useState('');
  const [contractDuration, setContractDuration] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    wrestler?.customRender || wrestler?.render ||
    (wrestler?.wrestler_id ? process.env.PUBLIC_URL + '/wrestlers/' + wrestler.wrestler_id + '-render.png' : null)
  );
  const [form, setForm] = useState({
    nick_name:             wrestler?.nick_name             || '',
    first_name:            wrestler?.first_name            || '',
    middle_name:           wrestler?.middle_name           || '',
    last_name:             wrestler?.last_name             || '',
    gender:                wrestler?.gender                || 'M',
    fight_class:           wrestler?.fight_class           || 'Fighter',
    fight_class_secondary: wrestler?.fight_class_secondary || '',
    heel:                  wrestler?.heel                  ?? false,
    starting_xp:           wrestler?.starting_xp           ?? 10,
    promo_skill:           wrestler?.promo_skill           ?? wrestler?.promo_level ?? 1,
    status:                wrestler?.status                || 'active',
    match_types:           wrestler?.match_types           || [],
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const priceNum = Number(contractPrice) || 0;
  const canAfford = priceNum <= budget;
  const canConfirm = contractPrice !== '' && canAfford && Number(contractDuration) > 0 && form.first_name.trim();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image too large (max 5MB)'); return; }
    setError(null);
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSign = async () => {
    if (!canConfirm) return;
    setSaving(true);
    try {
      let customRender = wrestler?.customRender || null;
      let customRenderPath = wrestler?.customRenderPath || null;
      if (pendingFile && userId) {
        const path = 'renders/' + userId + '/wrestler/' + (wrestler?.wrestler_id || Date.now()) + '_' + Date.now();
        const { ref: sRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
        const { storage } = await import('../firebase');
        const snapshot = await uploadBytes(sRef(storage, path), pendingFile);
        customRender = await getDownloadURL(snapshot.ref);
        customRenderPath = path;
      }
      const updatedWrestler = {
        ...wrestler,
        ...form,
        customRender,
        customRenderPath,
      };
      onConfirm({ wrestler: updatedWrestler, contractPrice: priceNum, contractDuration: Number(contractDuration) });
    } catch(e) {
      setError('Error: ' + e.message);
      setSaving(false);
    }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', color: '#fff', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', padding: '6px 10px', outline: 'none' };
  const labelStyle = { fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', marginBottom: '4px' };
  const FIGHT_CLASSES = ['Fighter', 'Bruiser', 'Cruiser', 'Specialist', 'Giant'];
  const MATCH_TYPES_LIST = [
    { id: 'steel-cage', label: 'Steel Cage' }, { id: 'hell-in-a-cell', label: 'Hell in a Cell' },
    { id: 'ladder', label: 'Ladder' }, { id: 'tlc', label: 'TLC' }, { id: 'tables', label: 'Tables' },
    { id: 'iron-man', label: 'Iron Man' }, { id: 'submission', label: 'Submission' },
    { id: 'last-man-standing', label: 'Last Man Standing' }, { id: 'i-quit', label: 'I Quit' },
    { id: 'first-blood', label: 'First Blood' }, { id: 'falls-count-anywhere', label: 'Falls Count Anywhere' },
    { id: 'casket', label: 'Casket' }, { id: 'ambulance', label: 'Ambulance' },
    { id: 'extreme-rules', label: 'Extreme Rules' }, { id: 'no-dq', label: 'No DQ' },
    { id: 'street-fight', label: 'Street Fight' }, { id: 'inferno', label: 'Inferno' },
  ];
  const toggleMatchType = (id) => {
    setForm(f => {
      const has = f.match_types.includes(id);
      // Max 2 specialties
      if (!has && f.match_types.length >= 2) return f;
      return { ...f, match_types: has ? f.match_types.filter(x => x !== id) : [...f.match_types, id] };
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)' }} onClick={!saving ? onClose : undefined} />
      <div style={{ position: 'relative', zIndex: 1, width: '720px', maxHeight: '90vh', background: 'rgba(8,8,8,0.98)', border: '1px solid ' + brandColor + '44', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.7)' }}>
        {/* Top bar */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + brandColor + ', transparent)' }} />

        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1rem', letterSpacing: '3px', color: '#fff' }}>SIGN SUPERSTAR</div>
            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.3)', marginTop: '2px', letterSpacing: '1px' }}>Adjust details before signing</div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', borderRadius: '4px', width: '28px', height: '28px' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'grid', gridTemplateColumns: '160px 1fr', gap: '24px' }}>

          {/* Left — render */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ width: '160px', height: '220px', background: 'radial-gradient(ellipse at bottom, ' + brandColor + '18 0%, transparent 70%), rgba(0,0,0,0.4)', border: '1px dashed rgba(255,255,255,0.12)', borderRadius: '8px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {previewUrl
                ? <img src={previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { e.target.style.display='none'; }} />
                : <div style={{ ...labelStyle, textAlign: 'center' }}>NO RENDER</div>}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={saving}
              style={{ ...inputStyle, cursor: 'pointer', textAlign: 'center', color: brandColor, border: '1px solid ' + brandColor + '44', background: brandColor + '0A', fontSize: '0.4rem', letterSpacing: '1px' }}>
              {previewUrl ? '↑ CHANGE' : '↑ UPLOAD'}
            </button>
          </div>

          {/* Right — fields */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Name row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[['Nick name', 'nick_name', false], ['First name', 'first_name', true], ['Middle name', 'middle_name', false], ['Last name', 'last_name', false]].map(([lbl, key, req]) => (
                <div key={key}>
                  <div style={labelStyle}>{lbl.toUpperCase()}{req && <span style={{ color: '#e74c3c' }}> *</span>}</div>
                  <input type="text" value={form[key]} onChange={e => update(key, e.target.value)} disabled={saving}
                    style={{ ...inputStyle, width: '100%' }} placeholder={lbl} />
                </div>
              ))}
            </div>

            {/* Attributes row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
              <div>
                <div style={labelStyle}>GENDER</div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {['M','F'].map(g => (
                    <button key={g} onClick={() => update('gender', g)} disabled={saving}
                      style={{ ...inputStyle, flex: 1, cursor: 'pointer', background: form.gender === g ? brandColor + '22' : 'rgba(255,255,255,0.04)', border: '1px solid ' + (form.gender === g ? brandColor : 'rgba(255,255,255,0.1)'), color: form.gender === g ? brandColor : 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                      {g === 'M' ? '♂' : '♀'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={labelStyle}>ROLE</div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {[['FACE', false], ['HEEL', true]].map(([lbl, val]) => (
                    <button key={lbl} onClick={() => update('heel', val)} disabled={saving}
                      style={{ ...inputStyle, flex: 1, cursor: 'pointer', background: form.heel === val ? (val ? 'rgba(231,76,60,0.18)' : 'rgba(46,204,113,0.18)') : 'rgba(255,255,255,0.04)', border: '1px solid ' + (form.heel === val ? (val ? '#e74c3c' : '#2ecc71') : 'rgba(255,255,255,0.1)'), color: form.heel === val ? (val ? '#e74c3c' : '#2ecc71') : 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={labelStyle}>XP</div>
                <input type="number" min="0" max="100" value={form.starting_xp} onChange={e => update('starting_xp', Number(e.target.value))} disabled={saving}
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <div style={labelStyle}>FIGHT CLASS</div>
                <select value={form.fight_class} onChange={e => update('fight_class', e.target.value)} disabled={saving}
                  style={{ ...inputStyle, width: '100%', colorScheme: 'dark' }}>
                  {FIGHT_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={labelStyle}>SECONDARY CLASS <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.32rem' }}>OPTIONAL</span></div>
                <select value={form.fight_class_secondary || ''} onChange={e => update('fight_class_secondary', e.target.value || '')} disabled={saving}
                  style={{ ...inputStyle, width: '100%', colorScheme: 'dark' }}>
                  <option value="">— None</option>
                  {FIGHT_CLASSES.filter(c => c !== form.fight_class).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <div style={labelStyle}>PROMO SKILL</div>
                <input type="number" min="0" max="5" value={form.promo_skill} onChange={e => update('promo_skill', Number(e.target.value))} disabled={saving}
                  style={{ ...inputStyle, width: '100%' }} />
              </div>
            </div>

            {/* Match Specialties */}
            <div>
              <div style={{ ...labelStyle, marginBottom: '6px' }}>
                MATCH SPECIALTIES
                <span style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.32rem', marginLeft: '6px' }}>MAX 2 — {form.match_types.length}/2</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {MATCH_TYPES_LIST.map(m => {
                  const sel = form.match_types.includes(m.id);
                  const disabled = saving || (!sel && form.match_types.length >= 2);
                  return (
                    <button key={m.id} onClick={() => !disabled && toggleMatchType(m.id)} disabled={disabled}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', letterSpacing: '0.5px', padding: '3px 9px', borderRadius: '3px', cursor: disabled ? 'not-allowed' : 'pointer',
                        background: sel ? 'rgba(212,175,55,0.15)' : 'transparent',
                        border: '1px solid ' + (sel ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.1)'),
                        color: sel ? '#d4af37' : disabled ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.5)',
                      }}>{m.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Divider */}
            <div style={{ height: '1px', background: 'rgba(255,255,255,0.06)', margin: '4px 0' }} />

            {/* Contract fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={labelStyle}>CONTRACT PRICE <span style={{ color: '#e74c3c' }}>*</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.35)' }}>$</span>
                    <input type="number" value={contractPrice} onChange={e => setContractPrice(e.target.value)} disabled={saving}
                      style={{ ...inputStyle, width: '100%', paddingLeft: '20px', border: '1px solid ' + (contractPrice !== '' && !canAfford ? '#e74c3c' : 'rgba(255,255,255,0.12)') }} placeholder="0" />
                  </div>
                </div>
                {contractPrice !== '' && !canAfford && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#e74c3c', marginTop: '3px' }}>⚠ Budget insuffisant</div>}
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.25)', marginTop: '3px' }}>Budget: <span style={{ color: '#2ecc71' }}>${budget.toLocaleString('fr-FR')}</span></div>
              </div>
              <div>
                <div style={labelStyle}>DURATION (weeks) <span style={{ color: '#e74c3c' }}>*</span></div>
                <input type="number" min="1" value={contractDuration} onChange={e => setContractDuration(e.target.value)} disabled={saving}
                  style={{ ...inputStyle, width: '100%' }} placeholder="1" />
                {contractDuration > 0 && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.25)', marginTop: '3px' }}>Expires W{(week || 1) + Number(contractDuration)}</div>}
              </div>
            </div>

            {error && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: '#e74c3c' }}>{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid rgba(255,255,255,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onClose} disabled={saving} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.35)', padding: '8px 18px', borderRadius: '5px', cursor: 'pointer' }}>CANCEL</button>
          <button onClick={handleSign} disabled={!canConfirm || saving}
            style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', background: canConfirm && !saving ? brandColor + '1A' : 'transparent', border: '1px solid ' + (canConfirm && !saving ? brandColor : 'rgba(255,255,255,0.1)'), color: canConfirm && !saving ? brandColor : 'rgba(255,255,255,0.2)', padding: '9px 28px', borderRadius: '5px', cursor: canConfirm && !saving ? 'pointer' : 'not-allowed' }}>
            {saving ? 'SIGNING...' : '✓ SIGN CONTRACT'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Contract Expiry Alert ─────────────────────────────────────────────
export const ContractExpiryAlert = ({ expiring, week, budget, brandColor, onRenew, onRelease, onClose }) => {
  const [selected, setSelected] = useState(null);
  const [renewType, setRenewType] = useState(null);
  const [renewPrice, setRenewPrice] = useState('');
  const [renewDuration, setRenewDuration] = useState('');

  if (!expiring || expiring.length === 0) return null;
  const w = selected || expiring[0];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)' }} />
      <div style={{ position: 'relative', zIndex: 1, width: '520px', background: 'rgba(8,8,8,0.97)', border: '1px solid rgba(231,76,60,0.4)', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #e74c3c, transparent)' }} />
        <div style={{ padding: '16px 22px 12px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#e74c3c', letterSpacing: '2px', marginBottom: '3px' }}>⚠ CONTRACT EXPIRING</div>
          <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.9rem', letterSpacing: '2px', color: '#fff' }}>WEEK {week}</div>
        </div>
        <div style={{ padding: '16px 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {expiring.length > 1 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {expiring.map(function(ew, i) { return (
                <div key={i} onClick={() => { setSelected(ew); setRenewType(null); setRenewPrice(''); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '4px', border: '1px solid ' + ((selected || expiring[0]).wrestler_id === ew.wrestler_id ? '#e74c3c' : 'rgba(255,255,255,0.15)'), background: (selected || expiring[0]).wrestler_id === ew.wrestler_id ? 'rgba(231,76,60,0.1)' : 'transparent', cursor: 'pointer' }}>
                  <img loading="lazy" src={getWrestlerRender(ew) || placeholder} alt="" style={{ width: '20px', height: '26px', objectFit: 'contain', objectPosition: 'top' }} />
                  <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#fff' }}>{getWrestlerName(ew, 'display')}</div>
                </div>
              ); })}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 14px', background: 'rgba(231,76,60,0.07)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: '8px' }}>
            <img loading="lazy" src={getWrestlerRender(w) || placeholder} alt="" style={{ width: '50px', height: '66px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.9))' }} />
            <div>
              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.8rem', letterSpacing: '1px', color: '#fff' }}>{getWrestlerNameUpper(w, 'display')}</div>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>{w.fight_class} · XP {w.starting_xp || 0}</div>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: '#e74c3c', marginTop: '2px' }}>Contract expires this week</div>
            </div>
          </div>
          {!renewType && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setRenewType('free')} style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.4)', color: '#2ecc71', padding: '10px', borderRadius: '6px', cursor: 'pointer' }}>🤝 RENEW FREE</button>
              <button onClick={() => setRenewType('paid')} style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37', padding: '10px', borderRadius: '6px', cursor: 'pointer' }}>💰 RENEW PAID</button>
              <button onClick={() => onRelease(w)} style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.4)', color: '#e74c3c', padding: '10px', borderRadius: '6px', cursor: 'pointer' }}>✕ RELEASE</button>
            </div>
          )}
          {renewType === 'free' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)' }}>NEW DURATION (weeks)</div>
              <input type="number" min="1" value={renewDuration} onChange={e => setRenewDuration(Number(e.target.value) || 1)}
                style={{ width: '120px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '7px 12px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none' }} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setRenewType(null)} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer' }}>← BACK</button>
                <button disabled={!renewDuration || Number(renewDuration) < 1} onClick={() => renewDuration && Number(renewDuration) >= 1 && onRenew(w, 0, Number(renewDuration))}
                  style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.5)', color: '#2ecc71', padding: '8px', borderRadius: '4px', cursor: 'pointer' }}>
                  ✓ CONFIRM FREE RENEWAL {renewDuration ? '(' + renewDuration + 'w)' : ''}
                </button>
              </div>
            </div>
          )}
          {renewType === 'paid' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>CONTRACT PRICE</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)' }}>$</span>
                    <input type="number" value={renewPrice} onChange={e => setRenewPrice(e.target.value)} placeholder="0"
                      style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '7px 12px 7px 24px', color: '#fff', fontSize: '0.55rem', fontFamily: "'Monda',sans-serif", outline: 'none' }} />
                  </div>
                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.35)' }}>Budget: <span style={{ color: '#2ecc71' }}>${budget.toLocaleString('fr-FR')}</span></span>
                </div>
              </div>
              <div>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)', marginBottom: '6px' }}>NEW DURATION (weeks)</div>
                <input type="number" min="1" value={renewDuration} onChange={e => setRenewDuration(Number(e.target.value) || 1)}
                  style={{ width: '120px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '6px', padding: '7px 12px', color: '#fff', fontSize: '0.6rem', fontFamily: "'Monda',sans-serif", outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={() => setRenewType(null)} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer' }}>← BACK</button>
                <button disabled={!renewPrice || Number(renewPrice) > budget}
                  onClick={() => renewPrice && Number(renewPrice) <= budget && renewDuration && Number(renewDuration) >= 1 && onRenew(w, Number(renewPrice), Number(renewDuration))}
                  style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: renewPrice && Number(renewPrice) <= budget ? 'rgba(212,175,55,0.12)' : 'transparent', border: '1px solid ' + (renewPrice && Number(renewPrice) <= budget ? 'rgba(212,175,55,0.5)' : 'rgba(255,255,255,0.1)'), color: renewPrice && Number(renewPrice) <= budget ? '#d4af37' : 'rgba(255,255,255,0.25)', padding: '8px', borderRadius: '4px', cursor: renewPrice && Number(renewPrice) <= budget ? 'pointer' : 'not-allowed' }}>
                  ✓ CONFIRM (${Number(renewPrice || 0).toLocaleString('fr-FR')} · {renewDuration}w)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────
export default function RecruitmentTab({ gameData, onBack, onUpdateGameData }) {
  const brand = gameData?.brand || '';
  const brandColor = BRAND_COLORS[brand] || '#d4af37';
  const roster = gameData?.roster || [];
  const budget = gameData?.budget || 0;
  const week = gameData?.week || 1;

  const [allWrestlers, setAllWrestlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterClass, setFilterClass] = useState('ALL');
  const [filterXP, setFilterXP] = useState('ALL');
  const [filterRole, setFilterRole] = useState('ALL');
  const [filterGender, setFilterGender] = useState('ALL');
  const [filterContract, setFilterContract] = useState('ALL');
  const [viewMode, setViewMode] = useState('grid');
  const [recruitTarget, setRecruitTarget] = useState(null);
  const [showCustomEditor, setShowCustomEditor] = useState(false);
  const [user] = useAuthState(auth);

  useEffect(() => {
    if (!user) return;
    var loadWrestlers = function() {
      var version = gameData?.gameVersion;
      var baseCollectionNames = version === '2k25' ? ['wrestlers_2k25']
        : version === '2k26' ? ['wrestlers_2k26']
        : ['wrestlers_2k25', 'wrestlers_2k26']; // legacy game, version unknown — merge both
      var promises = [
        getDocs(query(collection(db, 'officialWrestlers'), where('userId', '==', user.uid))),
        ...baseCollectionNames.map(function(name) { return getDocs(collection(db, name)); }),
      ];
      Promise.all(promises).then(function(snaps) {
        var custom = snaps[0].docs.map(d => d.data());
        var base = [];
        for (var i = 1; i < snaps.length; i++) base.push(...snaps[i].docs.map(d => d.data()));
        var merged = [];
        var seen = new Set();
        [...base, ...custom].forEach(function(w) {
          var id = String(w.wrestler_id);
          if (!seen.has(id)) { merged.push(w); seen.add(id); }
        });
        setAllWrestlers(merged);
        setLoading(false);
      }).catch(() => setLoading(false));
    };
    loadWrestlers();
    // Listen for global refresh events (e.g. after creating a custom wrestler in Settings)
    window.addEventListener('wrestlersUpdated', loadWrestlers);
    return () => window.removeEventListener('wrestlersUpdated', loadWrestlers);
  }, [user, gameData?.gameVersion]);

  const rosterIds = roster.map(w => w.wrestler_id);
  const available = allWrestlers
    .filter(w => w.status && w.status !== 'inactive' && w.status !== 'gm')
    .filter(w => !rosterIds.includes(w.wrestler_id))
    .filter(w => filterClass === 'ALL' || w.fight_class === filterClass)
    .filter(w => {
      if (filterXP === 'ALL') return true;
      var lvl = XP_LEVELS.find(function(l) { return l.label === filterXP; });
      var xp = Number(w.starting_xp) || 0;
      return lvl && xp >= lvl.min && xp <= lvl.max;
    })
    .filter(w => filterRole === 'ALL' || (filterRole === 'FACE' && !w.heel) || (filterRole === 'HEEL' && w.heel))
    .filter(w => filterGender === 'ALL' || (filterGender === 'MALE' && w.gender === 'M') || (filterGender === 'FEMALE' && w.gender === 'F'))
    .filter(w => filterContract === 'ALL' || w.status === filterContract.toLowerCase())
    .filter(w => !search || (wrestlerMatchesSearch(w, search)));

  const handleRecruit = ({ wrestler, contractPrice, contractDuration }) => {
    if (roster.length >= 70) {
      alert('Roster is full (70 superstars max) — release a superstar first.');
      setRecruitTarget(null);
      return;
    }
    var cleanWrestler = {
      ...wrestler,
      match_types: wrestler.match_types || [],
      fight_class_secondary: wrestler.fight_class_secondary || '',
    };
    var newRoster = [...roster, { ...cleanWrestler, contractPrice, contractDuration: Number(contractDuration), contractStart: week, contractEnd: week + Number(contractDuration) }];
    if (onUpdateGameData) onUpdateGameData({ ...gameData, roster: newRoster, budget: budget - contractPrice });
    setRecruitTarget(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '12px' }}>
      <style>{css}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
        {onBack && <button onClick={onBack} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.4)', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer' }}>← BACK</button>}
        <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.1rem', letterSpacing: '2px', color: '#fff' }}>RECRUITMENT</div>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.58rem', color: brandColor, border: '1px solid ' + brandColor + '44', borderRadius: '3px', padding: '3px 12px' }}>Budget: ${budget.toLocaleString('fr-FR')}</div>
        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>{available.length} available</div>
        <button onClick={() => setShowCustomEditor(true)} style={{ marginLeft: 'auto', fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37', padding: '6px 14px', borderRadius: '4px', cursor: 'pointer', letterSpacing: '1px' }}>✦ NEW SUPERSTAR</button>
        <div style={{ display: 'flex', gap: '4px' }}>
          {[{ key: 'grid', label: '⊞ GRID' }, { key: 'list', label: '☰ LIST' }].map(function(v) { return (
            <button key={v.key} onClick={() => setViewMode(v.key)}
              style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', background: viewMode === v.key ? 'rgba(255,255,255,0.1)' : 'transparent', border: '1px solid ' + (viewMode === v.key ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.1)'), color: viewMode === v.key ? '#fff' : 'rgba(255,255,255,0.4)', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}>
              {v.label}
            </button>
          ); })}
        </div>
      </div>

      {/* Search input */}
      <input style={{ flexShrink: 0, width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '8px 14px', color: '#fff', fontSize: '0.52rem', fontFamily: "'Monda',sans-serif", outline: 'none' }}
        placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />

      {/* Filter blocks */}
      <SearchBlock
        filterClass={filterClass} setFilterClass={setFilterClass}
        filterXP={filterXP} setFilterXP={setFilterXP}
        filterRole={filterRole} setFilterRole={setFilterRole}
        filterGender={filterGender} setFilterGender={setFilterGender}
        filterContract={filterContract} setFilterContract={setFilterContract}
        brandColor={brandColor}
      />

      {/* Results */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)' }}>Loading...</div>
        ) : available.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)' }}>No superstars match your filters</div>
        ) : viewMode === 'grid' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '14px' }}>
            {available.map(function(w, i) { return (
              <UnifiedWrestlerCard
                key={w.wrestler_id || i}
                wrestler={w}
                brandColor={brandColor}
                variant="recruitment"
                onClick={() => setRecruitTarget(w)}
                onSign={(ww) => setRecruitTarget(ww)}
              />
            ); })}
          </div>
        ) : (
          <div style={{ background: 'rgba(8,8,8,0.7)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '8px', overflow: 'hidden' }}>
            {available.map(function(w, i) { return (
              <RecruitRow key={w.wrestler_id || i} w={w} brandColor={brandColor} onClick={setRecruitTarget} />
            ); })}
          </div>
        )}
      </div>

      {showCustomEditor && (
        <CustomWrestlerEditor
          wrestler={null}
          userId={user?.uid}
          onClose={() => setShowCustomEditor(false)}
          onSaved={() => setShowCustomEditor(false)}
        />
      )}
      {recruitTarget && (
        <RecruitModal wrestler={recruitTarget} brandColor={brandColor} budget={budget} week={week} onConfirm={handleRecruit} onClose={() => setRecruitTarget(null)} userId={user?.uid} />
      )}
    </div>
  );
}
